import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { NewLabelInput } from '@/features/admin/hooks/useLabelsAdmin'

const EMPTY = { name: '', description: '', level: '1', color: '#22c55e' }

export interface CreateLabelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (input: NewLabelInput) => void
  submitting: boolean
}

export function CreateLabelDialog({
  open,
  onOpenChange,
  onSubmit,
  submitting,
}: CreateLabelDialogProps) {
  const { t } = useTranslation()
  const [form, setForm] = useState(EMPTY)

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) setForm(EMPTY)
        onOpenChange(isOpen)
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('admin.labels.createLabel')}</DialogTitle>
          <DialogDescription>{t('admin.labels.createLabelDescription')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>{t('admin.labels.name')}</Label>
            <Input
              placeholder={t('admin.labels.namePlaceholder')}
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('admin.labels.description')}</Label>
            <Input
              placeholder={t('admin.labels.descriptionPlaceholder')}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>{t('admin.labels.sensitivityLevel')}</Label>
              <Select value={form.level} onValueChange={level => setForm({ ...form, level })}>
                <SelectTrigger className="max-md:w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[300]">
                  {[1, 2, 3, 4, 5].map(lvl => (
                    <SelectItem key={lvl} value={String(lvl)}>
                      L{lvl} - {t(`admin.labels.levels.${lvl}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('admin.labels.color')}</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.color}
                  onChange={e => setForm({ ...form, color: e.target.value })}
                  className="w-8 h-8 rounded cursor-pointer bg-transparent border border-border"
                />
                <Input
                  value={form.color}
                  onChange={e => setForm({ ...form, color: e.target.value })}
                  className="font-mono text-xs flex-1"
                  placeholder="#22c55e"
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                name: form.name,
                description: form.description,
                level: Number.parseInt(form.level, 10),
                color: form.color,
              })
            }
            disabled={!form.name.trim() || submitting}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('admin.labels.createLabel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
